import { useState, useEffect, useCallback, useRef } from "react";
import { getProjetosCompleto } from "../../api/projetos.api";
import { atualizarTarefa, getComentarios, adicionarComentario, deletarComentario, getHistorico } from "../../api/tarefas.api";
import { getUser } from "../../api/auth.api";

/* ─── Helpers ─────────────────────────────────────────────────────── */

const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const DIAS_SEMANA = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

const STATUS_COR   = { "Nao Iniciada": "#64748b", "Em Andamento": "#60a5fa", "Bloqueada": "#f87171", "Concluida": "#4ade80" };
const STATUS_LABEL = { "Nao Iniciada": "Não Iniciada", "Em Andamento": "Em Andamento", "Bloqueada": "Bloqueada", "Concluida": "Concluída" };

function getCategoriaDia(tarefa) {
  if (tarefa.status === "Concluida" || !tarefa.data_entrega) return null;
  const hoje = new Date(new Date().toDateString());
  const prazo = new Date(tarefa.data_entrega);
  const diff  = Math.floor((prazo - hoje) / 864e5);
  if (diff < 0)  return { cor: "#f87171", ordem: 0, dias: Math.abs(diff) };
  if (diff <= 1) return { cor: "#fb923c", ordem: 1, dias: diff };
  if (diff <= 7) return { cor: "#fbbf24", ordem: 2, dias: diff };
  return         { cor: "#60a5fa",  ordem: 3, dias: diff };
}

function piorCat(ts) {
  return ts.reduce((pior, t) => {
    const cat = getCategoriaDia(t);
    if (!cat) return pior;
    if (!pior || cat.ordem < pior.ordem) return cat;
    return pior;
  }, null);
}

function fmtDate(str) {
  if (!str) return "—";
  const [y, m, d] = str.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function toKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}

function todayKey() { return toKey(new Date()); }

function diasLabel(cat) {
  if (!cat) return null;
  if (cat.ordem === 0) return `${cat.dias} dia${cat.dias !== 1 ? "s" : ""} de atraso`;
  if (cat.ordem === 1) return cat.dias === 0 ? "Vence hoje" : "Vence amanhã";
  return `${cat.dias} dias restantes`;
}

/* ─── Visual helpers (pura apresentação) ────────────────────────── */

const CAT_CORES = ["#E24B4A", "#EF9F27", "#534AB7", "#378ADD"];

function catCorNova(cat) {
  if (!cat) return "#5F5E5A";
  return CAT_CORES[cat.ordem] || "#5F5E5A";
}

const AVATAR_MAP = [
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "carlos",    bg: "#085041", color: "#9FE1CB" },
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
];

function avatarCores(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const low = nome.toLowerCase();
  for (const m of AVATAR_MAP) if (low.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}

function avatarInitial(nome) {
  if (!nome) return "?";
  return nome.trim()[0].toUpperCase();
}

/* ─── Form helper ────────────────────────────────────────────────── */

function emptyForm() {
  return {
    id_ticket: "", descritivo: "", responsavel: "",
    status: "Nao Iniciada", data_entrega: "", data_entrega_real: "",
    descritivo_restricao: "", responsavel_restricao: "", data_liberacao_restricao: "",
  };
}

/* ─── Página ─────────────────────────────────────────────────────── */

export default function TarefasCalendario() {
  const [tarefas,   setTarefas]   = useState([]);
  const [ticketMap, setTicketMap] = useState({});
  const [loading,   setLoading]   = useState(true);
  const [mes,       setMes]       = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [diaSel,    setDiaSel]    = useState(todayKey());
  const [countdown, setCountdown] = useState(60);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [modal,     setModal]     = useState(null);
  const [form,      setForm]      = useState(emptyForm());
  const [saving,    setSaving]    = useState(false);
  const [formErro,  setFormErro]  = useState("");
  const cntRef  = useRef(60);
  const isFirst = useRef(true);

  const load = useCallback(async (bust = false) => {
    try {
      const { projetos: rows, tarefas: allTarefas } = await getProjetosCompleto(bust, 2);
      const projetoMap = {};
      (rows || []).forEach(r => {
        if (r.id_projeto && !projetoMap[r.id_projeto])
          projetoMap[r.id_projeto] = { id: r.id_projeto, descricao: r.descricao, id_projeto_pai: r.id_projeto_pai };
      });
      const tMap = {};
      (rows || []).forEach(r => {
        if (r.ticketid && !tMap[r.ticketid]) {
          const pai = r.id_projeto_pai ? projetoMap[r.id_projeto_pai] : null;
          tMap[r.ticketid] = {
            id: r.ticketid, subject: r.subject || `#${r.ticketid}`,
            descricao_customizada: r.descricao_customizada || null,
            projeto: r.descricao, projeto_pai: pai?.descricao || null,
          };
        }
      });
      setTicketMap(tMap);
      setTarefas(allTarefas || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); isFirst.current = false; }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => {
      cntRef.current -= 1; setCountdown(cntRef.current);
      if (cntRef.current <= 0) { cntRef.current = 60; setCountdown(60); load(); }
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  /* ── Mapa dia → tarefas ── */
  const tarefasPorDia = {};
  const semPrazo = [];

  tarefas.forEach(t => {
    if (t.status === "Concluida") return;
    if (!t.data_entrega) { semPrazo.push(t); return; }
    const key = t.data_entrega.slice(0, 10);
    if (!tarefasPorDia[key]) tarefasPorDia[key] = [];
    tarefasPorDia[key].push(t);
  });

  /* ── KPI counts ── */
  const nAtrasadas = tarefas.filter(t => getCategoriaDia(t)?.ordem === 0).length;
  const nHoje      = tarefas.filter(t => getCategoriaDia(t)?.ordem === 1).length;
  const nSeteDias  = tarefas.filter(t => getCategoriaDia(t)?.ordem === 2).length;

  /* ── Grade do mês ── */
  const ano    = mes.getFullYear();
  const mesIdx = mes.getMonth();
  const primeiroDia  = new Date(ano, mesIdx, 1);
  const ultimoDia    = new Date(ano, mesIdx + 1, 0);
  const offsetInicio = primeiroDia.getDay();

  const celulas = [];
  for (let i = 0; i < offsetInicio; i++) celulas.push(null);
  for (let d = 1; d <= ultimoDia.getDate(); d++) celulas.push(new Date(ano, mesIdx, d));

  const nRows = Math.ceil(celulas.length / 7);

  /* ── Tarefas do dia selecionado ── */
  const tarefasDiaSel = tarefasPorDia[diaSel] || [];

  async function handleStatus(tarefa, novoStatus) {
    await atualizarTarefa(tarefa.id_tarefa, { ...tarefa, status: novoStatus });
    setTarefas(prev => prev.map(t => t.id_tarefa === tarefa.id_tarefa ? { ...t, status: novoStatus } : t));
  }

  function openEdit(tarefa) {
    setForm({
      id_ticket:               String(tarefa.id_ticket),
      descritivo:              tarefa.descritivo              || "",
      responsavel:             tarefa.responsavel             || "",
      status:                  tarefa.status                  || "Nao Iniciada",
      data_entrega:            tarefa.data_entrega            ? tarefa.data_entrega.slice(0, 10)            : "",
      data_entrega_real:       tarefa.data_entrega_real       ? tarefa.data_entrega_real.slice(0, 10)       : "",
      descritivo_restricao:    tarefa.descritivo_restricao    || "",
      responsavel_restricao:   tarefa.responsavel_restricao   || "",
      data_liberacao_restricao: tarefa.data_liberacao_restricao ? tarefa.data_liberacao_restricao.slice(0, 10) : "",
    });
    setFormErro("");
    setModal({ mode: "edit", tarefa });
  }

  async function handleSave() {
    if (!form.descritivo.trim()) { setFormErro("Descritivo é obrigatório."); return; }
    if (!form.id_ticket)         { setFormErro("Selecione um ticket.");      return; }
    setSaving(true); setFormErro("");
    try {
      const isBloq = form.status === "Bloqueada";
      const payload = {
        id_ticket:               Number(form.id_ticket),
        descritivo:              form.descritivo.trim(),
        responsavel:             form.responsavel.trim()  || null,
        status:                  form.status,
        data_entrega:            form.data_entrega             || null,
        data_entrega_real:       form.data_entrega_real        || null,
        descritivo_restricao:    isBloq ? (form.descritivo_restricao.trim()    || null) : null,
        responsavel_restricao:   isBloq ? (form.responsavel_restricao.trim()   || null) : null,
        data_liberacao_restricao: isBloq ? (form.data_liberacao_restricao      || null) : null,
      };
      await atualizarTarefa(modal.tarefa.id_tarefa, payload);
      setModal(null);
      await load(true);
    } catch (e) {
      setFormErro(e.response?.data?.message || "Erro ao salvar. Tente novamente.");
    } finally { setSaving(false); }
  }

  /* ── Dados para o modal ── */
  const ticketsList  = Object.values(ticketMap).map(tk => ({ id: tk.id, subject: tk.descricao_customizada || tk.subject }));
  const responsaveis = [...new Set(tarefas.map(t => t.responsavel).filter(Boolean))].sort();

  if (loading && isFirst.current)
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
        <span style={{ color:"#475569" }}>Carregando...</span>
      </div>
    );

  const today = todayKey();

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
        <div>
          <h1 style={pageTitle}>Calendário de Prazos</h1>
          <div style={pageSub}>Visualização mensal das tarefas por data de entrega</div>
        </div>
        <button style={refreshBtn} onClick={() => { cntRef.current=60; setCountdown(60); load(true); }}>
          ↻ {countdown}s
        </button>
      </div>

      {/* ── KPI cards ── */}
      <div style={kpiRow}>
        {[
          { cor:"#E24B4A", label:"Atrasadas",       n: nAtrasadas      },
          { cor:"#EF9F27", label:"Hoje/Amanhã",      n: nHoje           },
          { cor:"#534AB7", label:"Próximos 7 dias",  n: nSeteDias       },
          { cor:"#5F5E5A", label:"Sem prazo",         n: semPrazo.length },
        ].map(({ cor, label, n }) => (
          <div key={label} style={kpiCard(cor)}>
            <div style={{ position:"absolute", top:0, left:0, bottom:0, width:3, background:cor, borderRadius:"10px 0 0 10px" }} />
            <div style={{ fontSize:26, fontWeight:800, color:cor, lineHeight:1, minWidth:28, textAlign:"center" }}>{n}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.6)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.22)" }}>tarefa{n !== 1 ? "s" : ""}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Calendário + Detalhe ── */}
      <div style={mainGrid}>

        {/* Coluna calendário */}
        <div style={calCol}>

          {/* Navegação de mês */}
          <div style={navRow}>
            <button style={navBtn} onClick={() => setMes(m => new Date(m.getFullYear(), m.getMonth()-1, 1))}>‹</button>
            <span style={navTitle}>{MESES[mesIdx]} {ano}</span>
            <button style={navBtn} onClick={() => setMes(m => new Date(m.getFullYear(), m.getMonth()+1, 1))}>›</button>
          </div>

          {/* Cabeçalho dias da semana */}
          <div style={weekHeaderGrid}>
            {DIAS_SEMANA.map((d, i) => (
              <div key={d} style={{
                textAlign:"center", fontSize:10, fontWeight:700,
                padding:"4px 0", letterSpacing:1,
                textTransform:"uppercase",
                color: (i===0||i===6) ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.3)",
              }}>
                {d}
              </div>
            ))}
          </div>

          {/* Grade de dias */}
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(7, 1fr)",
            gridTemplateRows:`repeat(${nRows}, 1fr)`,
            gap:3,
            flex:1,
            minHeight:0,
          }}>
            {celulas.map((dia, i) => {
              if (!dia) return <div key={`pad-${i}`} />;

              const key       = toKey(dia);
              const ts        = tarefasPorDia[key] || [];
              const isHoje    = key === today;
              const isSel     = key === diaSel;
              const diaSemana = dia.getDay();
              const isWeekend = diaSemana === 0 || diaSemana === 6;
              const pior      = piorCat(ts);
              const novaCor   = catCorNova(pior);

              let bg     = isWeekend ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.15)";
              let border = "1px solid rgba(255,255,255,0.04)";

              if (ts.length > 0 && pior) { bg = `${novaCor}14`; border = `1px solid ${novaCor}35`; }
              if (isSel)  { bg = "rgba(83,74,183,0.22)"; border = "2px solid rgba(83,74,183,0.65)"; }
              if (isHoje && !isSel) { border = "2px solid rgba(83,74,183,0.45)"; }

              const isEmpty   = ts.length === 0 && !isHoje;
              const isHovered = hoveredKey === key && isEmpty;
              const finalBg   = isHovered ? "rgba(255,255,255,0.06)" : bg;
              const finalBord = isHovered ? "1px solid rgba(255,255,255,0.14)" : border;

              return (
                <div
                  key={key}
                  style={{
                    ...diaCell,
                    background: finalBg, border: finalBord,
                    cursor: ts.length > 0 ? "pointer" : "default",
                    transition:"all 0.15s ease",
                  }}
                  onMouseEnter={() => setHoveredKey(key)}
                  onMouseLeave={() => setHoveredKey(null)}
                  onClick={() => { if (ts.length > 0 || isHoje) setDiaSel(key); }}
                >
                  {/* Número do dia — badge circular para hoje */}
                  {isHoje ? (
                    <div style={{
                      width:24, height:24, borderRadius:"50%",
                      background:"#534AB7",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, fontWeight:800, color:"#fff", lineHeight:1, flexShrink:0,
                    }}>
                      {dia.getDate()}
                    </div>
                  ) : (
                    <span style={{
                      fontSize:12,
                      fontWeight: ts.length > 0 ? 700 : 400,
                      color: isSel
                        ? "#C4BFFF"
                        : isWeekend
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(255,255,255,0.4)",
                      lineHeight:1,
                    }}>
                      {dia.getDate()}
                    </span>
                  )}

                  {/* Badge de quantidade — círculo 18px */}
                  {ts.length > 0 && (
                    <div style={{
                      width:18, height:18, borderRadius:"50%",
                      background: novaCor,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:9, fontWeight:800, color:"#fff", lineHeight:1, flexShrink:0,
                    }}>
                      {ts.length}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legenda */}
          <div style={legendRow}>
            {[
              ["#E24B4A","Atrasada"],
              ["#EF9F27","Hoje/Amanhã"],
              ["#534AB7","7 dias"],
              ["#378ADD","Futuro"],
            ].map(([cor, label]) => (
              <span key={label} style={legendItem}>
                <span style={{ width:8, height:8, borderRadius:"50%", background:cor, display:"inline-block", flexShrink:0 }} />
                {label}
              </span>
            ))}
          </div>

        </div>

        {/* ── Coluna detalhe ── */}
        <div style={detailCol}>

          {/* Cabeçalho do painel */}
          <div style={detailHeader}>
            {diaSel ? (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:15, fontWeight:600, color:"#E5E7EB" }}>{fmtDate(diaSel)}</span>
                {tarefasDiaSel.length > 0 && (
                  <span style={{
                    fontSize:10, fontWeight:700, color:"#fff",
                    background:"#534AB7", borderRadius:20,
                    padding:"2px 9px", lineHeight:1.6,
                  }}>
                    {tarefasDiaSel.length}
                  </span>
                )}
              </div>
            ) : (
              <span style={{ color:"rgba(255,255,255,0.25)", fontSize:13 }}>Clique em um dia para ver as tarefas</span>
            )}
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {tarefasDiaSel.length === 0 && diaSel && (
              <div style={{ color:"rgba(255,255,255,0.2)", fontSize:13, padding:"24px 0", textAlign:"center" }}>
                Nenhuma tarefa neste dia
              </div>
            )}

            {tarefasDiaSel.map(t => {
              const cat      = getCategoriaDia(t);
              const novaCor  = catCorNova(cat);
              const tk       = ticketMap[t.id_ticket] || {};
              const desc     = tk.descricao_customizada || tk.subject || "";
              const dLabel   = diasLabel(cat);
              const av       = avatarCores(t.responsavel);

              // Borda esquerda por urgência / status
              const borderCor = (cat?.ordem === 0 || t.status === "Bloqueada")
                ? "#E24B4A"
                : t.status === "Em Andamento"
                  ? "#534AB7"
                  : t.status === "Concluida"
                    ? "#639922"
                    : "#5F5E5A";

              // Badge de data
              let dateBg = "#1a1d28", dateColor = "#6B7280";
              if (cat?.ordem === 0) { dateBg = "#501313"; dateColor = "#F09595"; }
              else if (cat?.ordem === 1) { dateBg = "#412402"; dateColor = "#FAC775"; }

              return (
                <div key={t.id_tarefa}
                  style={{ ...detailCard, borderLeft:`3px solid ${borderCor}`, borderRadius:"0 10px 10px 0", cursor:"pointer" }}
                  onClick={() => openEdit(t)}
                >

                  {/* Linha 1: tags + data */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:6, marginBottom:4 }}>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap", flex:1, minWidth:0 }}>
                      {tk.projeto_pai && <span style={projetoPaiChip}>{tk.projeto_pai}</span>}
                      {tk.projeto     && <span style={projetoChip}>{tk.projeto}</span>}
                    </div>
                    {t.data_entrega && (
                      <span style={{
                        fontSize:10, fontWeight:700,
                        background:dateBg, color:dateColor,
                        borderRadius:5, padding:"2px 7px",
                        flexShrink:0, whiteSpace:"nowrap",
                      }}>
                        {fmtDate(t.data_entrega)}
                      </span>
                    )}
                  </div>

                  {/* Linha 2: ticket */}
                  {desc && (
                    <div style={ticketChip}>#{t.id_ticket} · {desc}</div>
                  )}

                  {/* Linha 3: título */}
                  <div style={{ color:"#E5E7EB", fontSize:13, fontWeight:500, lineHeight:1.4, marginTop:4, marginBottom:6 }}>
                    {t.descritivo}
                  </div>

                  {/* Linha 4: avatar + responsável + urgência */}
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    {t.responsavel && (
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{
                          width:22, height:22, borderRadius:"50%",
                          background:av.bg, color:av.color,
                          fontSize:10, fontWeight:700,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          flexShrink:0,
                        }}>
                          {avatarInitial(t.responsavel)}
                        </div>
                        <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{t.responsavel}</span>
                      </div>
                    )}
                    <span style={{ fontSize:9, color: STATUS_COR[t.status] || "#64748b", marginLeft:4 }}>●</span>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)" }}>{STATUS_LABEL[t.status] || t.status}</span>
                    {cat && dLabel && (
                      <span style={{ fontSize:10, fontWeight:600, color:novaCor, marginLeft:"auto", whiteSpace:"nowrap" }}>
                        {dLabel}
                      </span>
                    )}
                  </div>

                  {/* Linha 5: botões de ação */}
                  {t.status !== "Concluida" && (
                    <div style={{ display:"flex", gap:6, marginTop:9, flexWrap:"wrap" }}>
                      {t.status !== "Em Andamento" && (
                        <button
                          style={{
                            padding:"4px 12px", borderRadius:6,
                            border:"1px solid rgba(83,74,183,0.4)",
                            background:"rgba(83,74,183,0.15)",
                            color:"#7B73E4", fontSize:10, fontWeight:600, cursor:"pointer",
                          }}
                          onClick={() => handleStatus(t, "Em Andamento")}
                        >
                          → Iniciar
                        </button>
                      )}
                      <button
                        style={{
                          padding:"4px 12px", borderRadius:6,
                          border:"1px solid rgba(99,153,34,0.4)",
                          background:"rgba(99,153,34,0.15)",
                          color:"#7BB83A", fontSize:10, fontWeight:600, cursor:"pointer",
                        }}
                        onClick={() => handleStatus(t, "Concluida")}
                      >
                        ✓ Concluir
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {semPrazo.length > 0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.25)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
                Sem prazo ({semPrazo.length})
              </div>
              {semPrazo.slice(0, 10).map(t => {
                const tk   = ticketMap[t.id_ticket] || {};
                const desc = tk.descricao_customizada || tk.subject || "";
                const av   = avatarCores(t.responsavel);
                return (
                  <div key={t.id_tarefa}
                    style={{ ...detailCard, opacity:0.6, borderLeft:"3px solid #5F5E5A", borderRadius:"0 10px 10px 0", marginBottom:6, cursor:"pointer" }}
                    onClick={() => openEdit(t)}
                  >
                    {desc && <div style={ticketChip}>#{t.id_ticket} · {desc}</div>}
                    <div style={{ color:"rgba(255,255,255,0.35)", fontSize:12, marginTop:3 }}>{t.descritivo}</div>
                    {t.responsavel && (
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:5 }}>
                        <div style={{
                          width:18, height:18, borderRadius:"50%",
                          background:av.bg, color:av.color,
                          fontSize:9, fontWeight:700,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          flexShrink:0,
                        }}>
                          {avatarInitial(t.responsavel)}
                        </div>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.28)" }}>{t.responsavel}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {semPrazo.length > 10 && (
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.2)", textAlign:"center", marginTop:4 }}>
                  +{semPrazo.length - 10} mais sem prazo
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* ── Modal de edição ── */}
      {modal && (
        <TarefaModal
          mode={modal.mode}
          idTarefa={modal.tarefa?.id_tarefa}
          form={form}
          setForm={setForm}
          tickets={ticketsList}
          responsaveis={responsaveis}
          erro={formErro}
          saving={saving}
          onSave={handleSave}
          onClose={() => setModal(null)}
          onComentariosChanged={() => {}}
        />
      )}
    </div>
  );
}

/* ─── TarefaModal ─────────────────────────────────────────────────── */

function TarefaModal({ mode, idTarefa, form, setForm, tickets, responsaveis, erro, saving, onSave, onClose, onComentariosChanged }) {
  const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isBloq = form.status === "Bloqueada";
  const user   = getUser();
  const autor  = user?.name || user?.login || "Usuário";

  const [comentarios,    setComentarios]    = useState([]);
  const [novoComentario, setNovoComentario] = useState("");
  const [savingComent,   setSavingComent]   = useState(false);
  const [historico,      setHistorico]      = useState([]);
  const [verHistorico,   setVerHistorico]   = useState(false);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    if (mode === "edit" && idTarefa) {
      getComentarios(idTarefa)
        .then(data => { setComentarios(data); onComentariosChanged?.(idTarefa, data.length); })
        .catch(() => setComentarios([]));
      getHistorico(idTarefa)
        .then(setHistorico)
        .catch(() => setHistorico([]));
    }
  }, [mode, idTarefa]); // eslint-disable-line

  async function handleAddComentario() {
    if (!novoComentario.trim()) return;
    setSavingComent(true);
    try {
      const novo = await adicionarComentario(idTarefa, autor, novoComentario.trim());
      const updated = [...comentarios, novo];
      setComentarios(updated);
      setNovoComentario("");
      onComentariosChanged?.(idTarefa, updated.length);
    } catch { /* silencioso */ }
    finally { setSavingComent(false); }
  }

  async function handleDeleteComentario(idComent) {
    try {
      await deletarComentario(idTarefa, idComent);
      const updated = comentarios.filter(c => c.id_comentario !== idComent);
      setComentarios(updated);
      onComentariosChanged?.(idTarefa, updated.length);
    } catch { /* silencioso */ }
  }

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalBox}>

        <div style={modalHeader}>
          <span style={modalTitle}>Editar Tarefa</span>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>

        <div style={modalBody}>

          <div>
            <label style={fieldLabel}>Ticket *</label>
            <select style={fieldSelect} value={form.id_ticket} onChange={e => set("id_ticket", e.target.value)}>
              <option value="">Selecione um ticket...</option>
              {tickets.map(t => (
                <option key={t.id} value={t.id}>#{t.id} — {t.subject}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={fieldLabel}>Descritivo *</label>
            <textarea
              style={{ ...fieldInput, height: 72, resize: "vertical" }}
              value={form.descritivo}
              onChange={e => set("descritivo", e.target.value)}
              placeholder="Descreva a tarefa..."
            />
          </div>

          <div style={twoCol}>
            <div>
              <label style={fieldLabel}>Responsável</label>
              <input
                style={fieldInput}
                list="cal-lista-responsaveis"
                value={form.responsavel}
                onChange={e => set("responsavel", e.target.value)}
                placeholder="Escolha ou digite..."
                autoComplete="off"
              />
              <datalist id="cal-lista-responsaveis">
                {responsaveis.map(r => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div>
              <label style={fieldLabel}>Status</label>
              <select style={fieldSelect} value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="Nao Iniciada">Não Iniciada</option>
                <option value="Em Andamento">Em Andamento</option>
                <option value="Bloqueada">Bloqueada</option>
                <option value="Concluida">Concluída</option>
              </select>
            </div>
          </div>

          <div style={twoCol}>
            <div>
              <label style={fieldLabel}>Prazo (data limite)</label>
              <input type="date" style={fieldInput} value={form.data_entrega} onChange={e => set("data_entrega", e.target.value)} />
            </div>
            <div>
              <label style={fieldLabel}>Concluída em (data real)</label>
              <input type="date" style={fieldInput} value={form.data_entrega_real} onChange={e => set("data_entrega_real", e.target.value)} />
            </div>
          </div>

          {isBloq && (
            <div style={restricaoSection}>
              <div style={restricaoSectionTitle}>⛔ Detalhes do Bloqueio</div>
              <div>
                <label style={fieldLabel}>Descrição do Bloqueio</label>
                <textarea
                  style={{ ...fieldInput, height: 60, resize: "vertical" }}
                  value={form.descritivo_restricao}
                  onChange={e => set("descritivo_restricao", e.target.value)}
                  placeholder="Qual é a restrição?"
                />
              </div>
              <div style={twoCol}>
                <div>
                  <label style={fieldLabel}>Com quem está</label>
                  <input
                    style={fieldInput}
                    list="cal-lista-responsaveis"
                    value={form.responsavel_restricao}
                    onChange={e => set("responsavel_restricao", e.target.value)}
                    placeholder="Escolha ou digite..."
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label style={fieldLabel}>Previsão de Liberação</label>
                  <input type="date" style={fieldInput} value={form.data_liberacao_restricao} onChange={e => set("data_liberacao_restricao", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {mode === "edit" && (
            <div style={comentariosSection}>
              <div style={comentariosTitle}>
                💬 Comentários {comentarios.length > 0 && <span style={comentBadge}>{comentarios.length}</span>}
              </div>
              {comentarios.length === 0 && (
                <div style={{ color: "#334155", fontSize: 11, fontStyle: "italic" }}>Nenhum comentário ainda.</div>
              )}
              {comentarios.map(c => (
                <div key={c.id_comentario} style={comentItem}>
                  <div style={comentHeader}>
                    <span style={comentAutor}>{c.autor}</span>
                    <span style={comentData}>{new Date(c.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    {c.autor === autor && (
                      <button style={comentDelBtn} onClick={() => handleDeleteComentario(c.id_comentario)} title="Remover">✕</button>
                    )}
                  </div>
                  <div style={comentTexto}>{c.comentario}</div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <input
                  style={{ ...fieldInput, flex: 1 }}
                  placeholder="Adicionar comentário..."
                  value={novoComentario}
                  onChange={e => setNovoComentario(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddComentario(); } }}
                />
                <button
                  style={{ ...btnSave, padding: "8px 14px", opacity: savingComent ? 0.6 : 1 }}
                  onClick={handleAddComentario}
                  disabled={savingComent || !novoComentario.trim()}
                >
                  {savingComent ? "..." : "Enviar"}
                </button>
              </div>
            </div>
          )}

          {mode === "edit" && (
            <div style={historicoSection}>
              <button style={historicoToggle} onClick={() => setVerHistorico(v => !v)}>
                🕐 Histórico de alterações ({historico.length})
                <span style={{ marginLeft: 6 }}>{verHistorico ? "▲" : "▼"}</span>
              </button>
              {verHistorico && historico.length === 0 && (
                <div style={{ color: "#334155", fontSize: 11, fontStyle: "italic", marginTop: 8, paddingLeft: 12 }}>
                  Nenhuma alteração registrada ainda.
                </div>
              )}
              {verHistorico && historico.length > 0 && (
                <div style={historicoLista}>
                  {historico.map(h => (
                    <div key={h.id_historico} style={historicoItem}>
                      <div style={historicoLine} />
                      <div style={{ flex: 1 }}>
                        <div style={historicoMeta}>
                          <span style={historicoCampo}>{h.campo}</span>
                          <span style={{ color: "#334155" }}>·</span>
                          <span style={{ color: "#475569" }}>{h.autor || "Sistema"}</span>
                          <span style={{ color: "#334155", marginLeft: "auto" }}>
                            {new Date(h.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <div style={historicoValores}>
                          {h.valor_anterior && <span style={valAnterior}>{h.valor_anterior}</span>}
                          {h.valor_anterior && <span style={{ color: "#334155" }}>→</span>}
                          <span style={valNovo}>{h.valor_novo}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {erro && <div style={erroBox}>{erro}</div>}
        </div>

        <div style={modalFooter}>
          <button style={btnCancel} onClick={onClose}>Cancelar</button>
          <button style={btnSave} onClick={onSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar Alterações"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page = {
  padding: "16px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  height: "100%",
  boxSizing: "border-box",
  overflow: "hidden",
};

const pageTitle = { color:"#F1F5F9", fontSize:20, fontWeight:700, margin:0 };
const pageSub   = { color:"rgba(255,255,255,0.3)", fontSize:12, marginTop:3 };

const refreshBtn = {
  padding:"6px 12px", borderRadius:8,
  border:"1px solid rgba(83,74,183,0.3)", background:"rgba(83,74,183,0.08)",
  color:"rgba(255,255,255,0.5)", fontSize:11, fontWeight:600, cursor:"pointer", flexShrink:0,
};

const kpiRow = { display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10, flexShrink:0 };

const kpiCard = cor => ({
  position:"relative", overflow:"hidden",
  background:"rgba(11,22,42,0.55)", border:`1px solid ${cor}22`,
  borderRadius:10, padding:"10px 14px 10px 18px",
  display:"flex", alignItems:"center", gap:12,
});

const mainGrid = {
  display:"grid", gridTemplateColumns:"1fr 320px", gap:16,
  flex:1, minHeight:0,
};

const calCol = {
  display:"flex", flexDirection:"column", gap:8,
  minHeight:0, overflow:"hidden",
};

const detailCol = {
  background:"rgba(11,22,42,0.5)", border:"1px solid rgba(255,255,255,0.05)",
  borderRadius:12, padding:14,
  display:"flex", flexDirection:"column", gap:10,
  overflowY:"auto", minHeight:0,
};

const navRow   = { display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 };
const navTitle = { fontSize:15, fontWeight:700, color:"#F1F5F9" };
const navBtn   = {
  padding:"4px 12px", borderRadius:7,
  border:"1px solid rgba(255,255,255,0.1)", background:"rgba(255,255,255,0.04)",
  color:"rgba(255,255,255,0.55)", fontSize:16, cursor:"pointer", lineHeight:1,
};

const weekHeaderGrid = { display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:3, flexShrink:0 };

const diaCell = {
  borderRadius:8, padding:"6px 3px",
  display:"flex", flexDirection:"column", alignItems:"center",
  gap:4, transition:"all 0.12s",
};

const legendRow  = { display:"flex", gap:14, flexWrap:"wrap", flexShrink:0 };
const legendItem = { display:"flex", alignItems:"center", gap:5, fontSize:10, color:"rgba(255,255,255,0.35)" };

const detailHeader = {
  paddingBottom:10,
  borderBottom:"1px solid rgba(255,255,255,0.06)",
  flexShrink:0,
};

const detailCard = {
  background:"rgba(11,22,42,0.65)", border:"1px solid rgba(255,255,255,0.06)",
  padding:"10px 12px",
};

const projetoPaiChip = { fontSize:9, fontWeight:600, color:"#a78bfa", background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.18)", borderRadius:4, padding:"1px 5px" };
const projetoChip    = { fontSize:9, fontWeight:600, color:"#22d3ee", background:"rgba(34,211,238,0.08)",  border:"1px solid rgba(34,211,238,0.18)", borderRadius:4, padding:"1px 5px" };
const ticketChip     = { fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.4)", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:4, padding:"1px 6px", display:"inline-block", marginBottom:3 };

/* ─── Estilos do modal ────────────────────────────────────────────── */

const backdrop = {
  position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
  display:"flex", alignItems:"center", justifyContent:"center",
  zIndex:1000, padding:16,
};
const modalBox = {
  background:"#08122a", border:"1px solid rgba(96,165,250,0.15)",
  borderRadius:16, width:"100%", maxWidth:560,
  display:"flex", flexDirection:"column", maxHeight:"90vh", overflow:"hidden",
};
const modalHeader = {
  display:"flex", justifyContent:"space-between", alignItems:"center",
  padding:"18px 22px 14px", borderBottom:"1px solid rgba(96,165,250,0.1)",
};
const modalTitle    = { color:"#f1f5f9", fontWeight:700, fontSize:16 };
const modalCloseBtn = { background:"transparent", border:"none", color:"#475569", fontSize:16, cursor:"pointer" };
const modalBody     = { padding:"18px 22px", display:"flex", flexDirection:"column", gap:12, overflowY:"auto" };
const modalFooter   = { display:"flex", justifyContent:"flex-end", gap:10, padding:"14px 22px", borderTop:"1px solid rgba(96,165,250,0.1)" };

const fieldLabel  = { display:"block", color:"#94a3b8", fontSize:11, fontWeight:600, marginBottom:5 };
const fieldInput  = { width:"100%", padding:"8px 11px", borderRadius:8, border:"1px solid rgba(96,165,250,0.15)", background:"rgba(11,22,42,0.7)", color:"#e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box", colorScheme:"dark" };
const fieldSelect = { ...fieldInput, cursor:"pointer" };
const twoCol      = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 };

const restricaoSection      = { background:"rgba(248,113,113,0.06)", border:"1px solid rgba(248,113,113,0.2)", borderRadius:10, padding:14, display:"flex", flexDirection:"column", gap:10 };
const restricaoSectionTitle = { color:"#f87171", fontSize:12, fontWeight:700 };

const comentariosSection = { borderTop:"1px solid rgba(96,165,250,0.1)", paddingTop:12, display:"flex", flexDirection:"column", gap:8 };
const comentariosTitle   = { fontSize:11, fontWeight:700, color:"#60a5fa", textTransform:"uppercase", letterSpacing:0.8, display:"flex", alignItems:"center", gap:6 };
const comentBadge        = { background:"rgba(96,165,250,0.15)", color:"#60a5fa", borderRadius:8, padding:"0px 6px", fontSize:10 };
const comentItem         = { background:"rgba(96,165,250,0.05)", border:"1px solid rgba(96,165,250,0.1)", borderRadius:8, padding:"8px 10px" };
const comentHeader       = { display:"flex", alignItems:"center", gap:8, marginBottom:4 };
const comentAutor        = { fontSize:11, fontWeight:700, color:"#60a5fa" };
const comentData         = { fontSize:10, color:"#334155", flex:1 };
const comentDelBtn       = { background:"none", border:"none", color:"#334155", cursor:"pointer", fontSize:11, padding:"0 2px", lineHeight:1 };
const comentTexto        = { fontSize:12, color:"#94a3b8", lineHeight:1.5 };

const historicoSection = { borderTop:"1px solid rgba(96,165,250,0.08)", paddingTop:10 };
const historicoToggle  = { background:"none", border:"none", cursor:"pointer", color:"#475569", fontSize:11, fontWeight:600, display:"flex", alignItems:"center", gap:4, padding:0, width:"100%" };
const historicoLista   = { display:"flex", flexDirection:"column", gap:8, marginTop:10 };
const historicoItem    = { display:"flex", gap:10, alignItems:"flex-start" };
const historicoLine    = { width:2, minHeight:36, background:"rgba(96,165,250,0.15)", borderRadius:2, flexShrink:0, marginTop:4 };
const historicoMeta    = { display:"flex", alignItems:"center", gap:6, fontSize:10, marginBottom:3 };
const historicoCampo   = { fontSize:10, fontWeight:700, color:"#60a5fa", background:"rgba(96,165,250,0.1)", borderRadius:4, padding:"1px 5px" };
const historicoValores = { display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" };
const valAnterior      = { fontSize:11, color:"#475569", textDecoration:"line-through" };
const valNovo          = { fontSize:11, color:"#e2e8f0", fontWeight:500 };

const erroBox   = { color:"#f87171", fontSize:12, background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.2)", borderRadius:8, padding:"8px 12px" };
const btnCancel = { padding:"9px 18px", borderRadius:9, border:"1px solid rgba(96,165,250,0.15)", background:"transparent", color:"#475569", fontSize:13, cursor:"pointer" };
const btnSave   = { padding:"9px 20px", borderRadius:9, border:"none", background:"linear-gradient(135deg, #3b82f6, #06b6d4)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" };
